import { create } from 'zustand'
import type { Keyword, CreateKeywordInput, AlertHistoryEntry, ApiResponse } from '../types'
import { api } from '../lib/api'

interface AlertState {
  keywords: Keyword[]
  alertHistory: AlertHistoryEntry[]
  isLoading: boolean
  error: string | null
  soundEnabled: boolean
  browserNotificationsEnabled: boolean

  // Actions
  fetchKeywords: () => Promise<void>
  addKeyword: (input: CreateKeywordInput) => Promise<ApiResponse<Keyword>>
  updateKeyword: (id: number, updates: Partial<Keyword>) => Promise<ApiResponse<Keyword>>
  deleteKeyword: (id: number) => Promise<ApiResponse<void>>
  toggleKeyword: (id: number) => Promise<void>
  fetchAlertHistory: () => Promise<void>
  setSoundEnabled: (enabled: boolean) => void
  setBrowserNotificationsEnabled: (enabled: boolean) => void
  testAlert: () => void
}

export const useAlertStore = create<AlertState>((set, get) => ({
  keywords: [],
  alertHistory: [],
  isLoading: false,
  error: null,
  soundEnabled: true,
  browserNotificationsEnabled: true,

  fetchKeywords: async () => {
    set({ isLoading: true, error: null })

    try {
      const response = await api.get<Keyword[]>('/api/keywords')

      if (response.success && response.data) {
        set({ keywords: response.data, isLoading: false })
      } else {
        set({ error: response.error || 'Failed to fetch keywords', isLoading: false })
      }
    } catch (err) {
      set({ error: 'Network error', isLoading: false })
    }
  },

  addKeyword: async (input) => {
    try {
      const response = await api.post<Keyword>('/api/keywords', input)

      if (response.success && response.data) {
        set((state) => ({ keywords: [...state.keywords, response.data!] }))
      }

      return response
    } catch (err) {
      return { success: false, error: 'Network error' }
    }
  },

  updateKeyword: async (id, updates) => {
    try {
      const response = await api.put<Keyword>(`/api/keywords/${id}`, updates)

      if (response.success && response.data) {
        set((state) => ({
          keywords: state.keywords.map((k) =>
            k.id === id ? { ...k, ...response.data } : k
          ),
        }))
      }

      return response
    } catch (err) {
      return { success: false, error: 'Network error' }
    }
  },

  deleteKeyword: async (id) => {
    try {
      const response = await api.delete<void>(`/api/keywords/${id}`)

      if (response.success) {
        set((state) => ({
          keywords: state.keywords.filter((k) => k.id !== id),
        }))
      }

      return response
    } catch (err) {
      return { success: false, error: 'Network error' }
    }
  },

  toggleKeyword: async (id) => {
    const keyword = get().keywords.find((k) => k.id === id)
    if (keyword) {
      await get().updateKeyword(id, { enabled: !keyword.enabled })
    }
  },

  fetchAlertHistory: async () => {
    try {
      const response = await api.get<AlertHistoryEntry[]>('/api/alerts/history')

      if (response.success && response.data) {
        set({ alertHistory: response.data })
      }
    } catch (err) {
      console.error('Failed to fetch alert history', err)
    }
  },

  setSoundEnabled: (enabled) => {
    set({ soundEnabled: enabled })
    api.put('/api/settings', { sound_enabled: enabled })
  },

  setBrowserNotificationsEnabled: (enabled) => {
    set({ browserNotificationsEnabled: enabled })
    api.put('/api/settings', { browser_notifications_enabled: enabled })
  },

  testAlert: () => {
    const { soundEnabled, browserNotificationsEnabled } = get()

    if (soundEnabled) {
      const audio = new Audio('/sounds/alert.mp3')
      audio.play().catch(() => {})
    }

    if (browserNotificationsEnabled && Notification.permission === 'granted') {
      new Notification('X Monitor Test Alert', {
        body: 'This is a test notification',
        icon: '/favicon.svg',
      })
    }
  },
}))
